import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const ACCOUNT_FIELDS = [
  ['asset_cost_account_id', 'Asset cost account', 'The balance-sheet account that holds the capitalized cost.'],
  ['accumulated_depreciation_account_id', 'Accumulated depreciation account', 'Contra-asset account used for depreciation.'],
  ['depreciation_expense_account_id', 'Depreciation expense account', 'Profit-and-loss account used by depreciation runs.'],
  ['asset_clearing_account_id', 'Fixed Asset Clearing account', 'Temporary account used when supplier invoices are posted before capitalization.'],
];

export default function AssetCategoryForm({ value, onChange, accounts = [] }) {
  const set = (key, val) => onChange({ ...value, [key]: val });
  const setMapping = (key, val) => onChange({
    ...value,
    accounting_mappings: { ...(value.accounting_mappings || {}), [key]: val },
  });
  const selectableAccounts = accounts.filter((account) => account?.id && account?.is_header !== true && account?.is_active !== false);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
        Category code is generated automatically when you create the record.
      </div>

      <div><Label>Name</Label><Input value={value.name || ''} onChange={(event) => set('name', event.target.value)} /></div>
      <div><Label>Asset number prefix</Label><Input value={value.asset_number_prefix || ''} onChange={(event) => set('asset_number_prefix', event.target.value.toUpperCase())} placeholder="e.g. MOB" /></div>
      <div><Label>Useful life (months)</Label><Input type="number" min="1" value={value.useful_life_months || 60} onChange={(event) => set('useful_life_months', Number(event.target.value))} /></div>
      <div>
        <Label>Depreciation method</Label>
        <Select value={value.depreciation_method || 'straight_line'} onValueChange={(val) => set('depreciation_method', val)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="straight_line">Straight line</SelectItem>
            <SelectItem value="declining_balance">Declining balance</SelectItem>
            <SelectItem value="double_declining_balance">Double declining balance</SelectItem>
            <SelectItem value="units_of_production">Units of production</SelectItem>
            <SelectItem value="no_depreciation">No depreciation</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="sm:col-span-2 border-t pt-4">
        <p className="font-semibold text-slate-900">Financial account mappings</p>
        <p className="mt-1 text-xs text-slate-500">Select the accounts used for supplier invoice posting, capitalization and depreciation. These may be inherited from Fixed Asset Settings where the category mapping is left blank.</p>
      </div>

      {ACCOUNT_FIELDS.map(([key, label, help]) => (
        <div key={key} className={key === 'asset_clearing_account_id' ? 'sm:col-span-2' : ''}>
          <Label>{label}</Label>
          <Select value={value.accounting_mappings?.[key] || undefined} onValueChange={(val) => setMapping(key, val)}>
            <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
            <SelectContent>
              {selectableAccounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>{account.code} — {account.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-slate-500">{help}</p>
        </div>
      ))}

      <div className="flex items-center gap-3"><Switch checked={value.is_depreciable !== false} onCheckedChange={(checked) => set('is_depreciable', checked)} /><Label>Depreciable</Label></div>
      <div className="flex items-center gap-3"><Switch checked={value.is_active !== false} onCheckedChange={(checked) => set('is_active', checked)} /><Label>Active</Label></div>
    </div>
  );
}
